import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang } from "@/contexts/LanguageContext";
import { QUOTE_FORM } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  company: "",
  contact_name: "",
  email: "",
  phone: "",
  country: "",
  categories: [],
  products: "",
  estimated_quantity: "",
  message: "",
};

const QuoteForm = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (key) => (e) =>
    setData((d) => ({ ...d, [key]: e.target ? e.target.value : e }));

  const toggleCategory = (cat) => () =>
    setData((d) => ({
      ...d,
      categories: d.categories.includes(cat)
        ? d.categories.filter((c) => c !== cat)
        : [...d.categories, cat],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSuccess(false);
    try {
      await axios.post(`${API}/quote`, { ...data, language: lang });
      setSuccess(true);
      setData(initial);
      toast.success(t.quote.success);
    } catch (err) {
      console.error(err);
      toast.error(t.quote.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="section-pad bg-[#F3F3EF]">
      <div className="container-cust grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left intro */}
        <div className="lg:col-span-5">
          <p className="eyebrow mb-5">
            <span className="eyebrow-line"></span>
            {t.quote.eyebrow}
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1A2421] leading-tight">
            {t.quote.title}
          </h2>
          <div className="divider-line my-8 max-w-sm" />
          <p className="text-[#4A5D58] text-base md:text-lg leading-relaxed">
            {t.quote.subtitle}
          </p>

          <div className="hidden lg:block mt-12 pl-6 border-l-2 border-[#D4AF37]">
            <p className="font-serif-display text-xl text-[#1A2421] leading-snug">
              “Un account manager dedicato vi seguirà dall'ordine alla consegna.”
            </p>
            <p className="mt-3 text-xs uppercase tracking-widest text-[#4A5D58]">
              Food &amp; Beverage Services S.R.L.
            </p>
          </div>
        </div>

        {/* Right form */}
        <div className="lg:col-span-7">
          <form
            data-testid={QUOTE_FORM.root}
            onSubmit={onSubmit}
            className="bg-white p-7 md:p-10 rounded-sm border border-[#E2E2DB] space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t.quote.labels.company + " *"}>
                <Input
                  data-testid={QUOTE_FORM.company}
                  required
                  value={data.company}
                  onChange={set("company")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
              <Field label={t.quote.labels.contactName + " *"}>
                <Input
                  data-testid={QUOTE_FORM.contactName}
                  required
                  value={data.contact_name}
                  onChange={set("contact_name")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
              <Field label={t.quote.labels.email + " *"}>
                <Input
                  data-testid={QUOTE_FORM.email}
                  required
                  type="email"
                  value={data.email}
                  onChange={set("email")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
              <Field label={t.quote.labels.phone}>
                <Input
                  data-testid={QUOTE_FORM.phone}
                  value={data.phone}
                  onChange={set("phone")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
              <Field label={t.quote.labels.country + " *"} className="sm:col-span-2">
                <Input
                  data-testid={QUOTE_FORM.country}
                  required
                  value={data.country}
                  onChange={set("country")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#4A5D58]">
                {t.quote.labels.categories}
              </Label>
              <div className="mt-3 flex flex-wrap gap-3">
                <CategoryChip
                  testId={QUOTE_FORM.catBeverages}
                  active={data.categories.includes("beverages")}
                  onClick={toggleCategory("beverages")}
                  label={t.quote.labels.catBeverages}
                />
                <CategoryChip
                  testId={QUOTE_FORM.catSweet}
                  active={data.categories.includes("sweet")}
                  onClick={toggleCategory("sweet")}
                  label={t.quote.labels.catSweet}
                />
                <CategoryChip
                  testId={QUOTE_FORM.catSalty}
                  active={data.categories.includes("salty")}
                  onClick={toggleCategory("salty")}
                  label={t.quote.labels.catSalty}
                />
                <CategoryChip
                  testId={QUOTE_FORM.catOther}
                  active={data.categories.includes("other")}
                  onClick={toggleCategory("other")}
                  label={t.quote.labels.catOther}
                />
              </div>
            </div>

            <Field label={t.quote.labels.products}>
              <Textarea
                data-testid={QUOTE_FORM.products}
                rows={4}
                placeholder={t.quote.labels.productsPlaceholder}
                value={data.products}
                onChange={set("products")}
                className="bg-transparent border border-[#E2E2DB] rounded-sm focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
              />
            </Field>

            <Field label={t.quote.labels.quantity}>
              <Input
                data-testid={QUOTE_FORM.quantity}
                placeholder={t.quote.labels.quantityPlaceholder}
                value={data.estimated_quantity}
                onChange={set("estimated_quantity")}
                className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
              />
            </Field>

            <Field label={t.quote.labels.message}>
              <Textarea
                data-testid={QUOTE_FORM.message}
                rows={3}
                placeholder={t.quote.labels.messagePlaceholder}
                value={data.message}
                onChange={set("message")}
                className="bg-transparent border border-[#E2E2DB] rounded-sm focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
              />
            </Field>

            <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
              <p className="text-xs text-[#4A5D58]">{t.quote.requiredHint}</p>
              <Button
                data-testid={QUOTE_FORM.submit}
                type="submit"
                disabled={submitting}
                className="bg-[#0E5A4E] hover:bg-[#0B4A40] text-white rounded-full px-7 h-12 text-sm"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.quote.submitting}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t.quote.submit}
                  </>
                )}
              </Button>
            </div>

            {success && (
              <div
                data-testid={QUOTE_FORM.success}
                className="mt-2 p-4 bg-[#0E5A4E]/5 border border-[#0E5A4E]/20 text-[#0E5A4E] text-sm rounded-sm"
              >
                {t.quote.success}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-2 ${className}`}>
    <Label className="text-xs uppercase tracking-widest text-[#4A5D58]">{label}</Label>
    {children}
  </div>
);

const CategoryChip = ({ testId, active, onClick, label }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-4 h-10 rounded-full text-sm transition-colors border ${
      active
        ? "bg-[#0E5A4E] text-white border-[#0E5A4E]"
        : "bg-white text-[#4A5D58] border-[#E2E2DB] hover:border-[#0E5A4E] hover:text-[#0E5A4E]"
    }`}
  >
    {label}
  </button>
);

export default QuoteForm;
